import { useApp } from '../../state/AppContext';
import Icon from '../../lib/Icon';
import { cardPad } from '../../lib/ui';
import type { FeatureFlags } from '../../lib/types';

export default function Settings() {
  const { state, set, L } = useApp();

  const doaSettings = [
    { level: L('Officer', 'เจ้าหน้าที่'), max: '≤ $500,000' },
    { level: L('Manager', 'ผู้จัดการ'), max: '≤ $2,000,000' },
    { level: L('Director', 'ผู้อำนวยการ'), max: '≤ $5,000,000' },
    { level: L('Executive Committee', 'คณะกรรมการบริหาร'), max: '> $5,000,000' },
  ];
  const aiWeights = [
    { label: L('Price', 'ราคา'), val: 40 },
    { label: L('Quality', 'คุณภาพ'), val: 25 },
    { label: L('Delivery', 'การจัดส่ง'), val: 15 },
    { label: L('Performance', 'ผลงาน'), val: 12 },
    { label: L('Compliance', 'การปฏิบัติตามข้อกำหนด'), val: 8 },
  ].map((w) => ({ ...w, w: w.val + '%' }));

  const featDefs: { key: keyof FeatureFlags; label: string; desc: string }[] = [
    { key: 'aiScoring', label: L('AI Bid Scoring', 'การให้คะแนนประมูลด้วย AI'), desc: L('Score sealed bids on the weighted matrix above.', 'ให้คะแนนการประมูลแบบปิดตามเมทริกซ์น้ำหนักด้านบน') },
    { key: 'collusion', label: L('Collusion Detection', 'การตรวจจับการสมรู้ร่วมคิด'), desc: L('Shared-director network analysis before award.', 'วิเคราะห์เครือข่ายกรรมการร่วมก่อนมอบรางวัล') },
    { key: 'rpt', label: L('Related-Party Transactions', 'ธุรกรรมกับบุคคลที่เกี่ยวโยงกัน'), desc: L('Flag transactions with group-affiliated parties.', 'ทำเครื่องหมายธุรกรรมกับบุคคลในกลุ่มที่เกี่ยวข้อง') },
    { key: 'sod', label: L('Segregation of Duties', 'การแบ่งแยกหน้าที่'), desc: L('Block the same user from creating and approving.', 'ป้องกันไม่ให้ผู้ใช้คนเดียวกันสร้างและอนุมัติ') },
    { key: 'mfa', label: L('Multi-Factor Authentication', 'การยืนยันตัวตนหลายปัจจัย'), desc: L('Require MFA for privileged roles.', 'บังคับใช้ MFA สำหรับบทบาทที่มีสิทธิ์พิเศษ') },
    { key: 'onboarding', label: L('First-Time Onboarding', 'การเริ่มต้นใช้งานครั้งแรก'), desc: L('Show the welcome tour and checklist on first login.', 'แสดงทัวร์ต้อนรับและรายการตรวจสอบเมื่อเข้าสู่ระบบครั้งแรก') },
  ];
  const features = featDefs.map((f) => {
    const on = !!state.featureFlags[f.key];
    return {
      key: f.key,
      label: f.label,
      desc: f.desc,
      track: on ? 'var(--color-primary)' : 'var(--color-ink-100)',
      knobX: on ? '18px' : '2px',
      onToggle: () => set((s) => ({ featureFlags: { ...s.featureFlags, [f.key]: !s.featureFlags[f.key] } })),
    };
  });

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--color-success-bg)', border: '1px solid var(--color-success)', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 13, color: 'var(--color-success)' }}>
        <Icon name="check" />
        <span style={{ color: 'var(--color-fg-1)' }}>{L('Settings are synced. Changes are logged to the audit trail.', 'การตั้งค่าถูกซิงค์แล้ว การเปลี่ยนแปลงถูกบันทึกในบันทึกการตรวจสอบ')}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        <div style={cardPad(20)}>
          <div style={{ fontWeight: 700, marginBottom: 14 }}>{L('DOA Threshold Matrix', 'เมทริกซ์เกณฑ์ DOA')}</div>
          {doaSettings.map((d) => (
            <div key={d.level} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--color-border)' }}>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{d.level}</span>
              <span style={{ fontSize: 12, color: 'var(--color-fg-3)' }}>{L('up to $', 'สูงสุด $')}</span>
              <input defaultValue={d.max} style={{ width: 110, padding: '6px 10px', border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-mono)', textAlign: 'right' }} />
            </div>
          ))}
        </div>
        <div style={cardPad(20)}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <span style={{ fontWeight: 700 }}>{L('AI Scoring Weights', 'น้ำหนักคะแนน AI')}</span>
            <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-mono)', padding: '3px 10px', borderRadius: 999, background: 'var(--color-success-bg)', color: 'var(--color-success)' }}>{L('Total 100%', 'รวม 100%')}</span>
          </div>
          {aiWeights.map((w) => (
            <div key={w.label} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                <span style={{ fontWeight: 500 }}>{w.label}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{w.val}%</span>
              </div>
              <div style={{ height: 8, borderRadius: 999, background: 'var(--color-ink-50)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: w.w, background: 'var(--color-blue)' }} />
              </div>
            </div>
          ))}
        </div>
        <div style={{ ...cardPad(20), gridColumn: '1 / -1' }}>
          <div style={{ fontWeight: 700, marginBottom: 14 }}>{L('Feature Configuration', 'การตั้งค่าฟีเจอร์')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {features.map((f) => (
              <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{f.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-fg-3)' }}>{f.desc}</div>
                </div>
                <button onClick={f.onToggle} style={{ position: 'relative', width: 40, height: 22, borderRadius: 999, background: f.track, flex: 'none', transition: 'background .2s' }}>
                  <span style={{ position: 'absolute', top: 2, left: f.knobX, width: 18, height: 18, borderRadius: 999, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.2)', transition: 'left .2s' }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
